import moment from 'moment'
import { useContext, useEffect, useRef } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { CardCommercialOffers } from './ui'
import { CardCommercialOfferTabPrimary } from './ui.tab.primary'
import { FieldsErrorType, ICardCommercialOffers, ICardCommercialOffersContainer, ICardCommercialOffersTabPrimary, IFieldsError } from './types'

import CommonService from '@services/common.service'

import { CardModeType } from '@smart/common.type'
import { CardCommercialOffersProgramContainer, ICardCommercialOffersProgramContainer } from '@smart/cardKP_Program'
import { CardCommercialOffersGenerateDocContainer, ICardCommercialOffersGenerateDocContainer } from '@smart/cardKP_GenerateDoc'

import { useValue } from '@hooks/useValue'
import { useCardSaveGuard } from '@hooks/useCardSaveGuard'
import { useFieldsErrorAnimation } from '@hooks/useFieldsErrorAnimation'
import { validFormData } from '@hooks/useForm'

import { useDict } from '@api/dict/state'
import { ISitesDbNameList } from '@api/dict/state.types'
import { useCommercialOffers } from '@api/commercialOffers/state'
import { CommercialOfferCardFilesListType, CommercialOfferCardProgramsListType, ICommercialOfferCard } from '@api/commercialOffers/state.types'

import { DictNameType } from '@smart/common.type'

import { IModalConfirm, ModalConfirm } from '@primitive/modal.confirm'
import { IModalPreview, ModalPreview } from '@primitive/modal.preview'

import { NotificationContext, notifyOpen } from '@ui/notification/notification.provider'


type CommercialOfferFormState = {
    applicationID: number | ''
    siteDbName: string
    commercialOffersStatusID: number | ''
    commercialOfferDate: string
    commercialValidPeriodDate: string
    toWhom: string
    phone: string
    email: string
    comment: string
    dateNextCall: string
    timeNextCall: string
    pendingPrograms: CommercialOfferCardProgramsListType[]
}

const DEFAULT_COMMERCIAL_OFFER_FORM_STATE: CommercialOfferFormState = {
    applicationID: '',
    siteDbName: 'modern_academy',
    commercialOffersStatusID: '',
    commercialOfferDate: '',
    commercialValidPeriodDate: '',
    toWhom: '',
    phone: '',
    email: '',
    comment: '',
    dateNextCall: '',
    timeNextCall: '',
    pendingPrograms: [],
}

const createCommercialOfferFormSnapshot = (state: CommercialOfferFormState) => JSON.stringify(state)

const getCommercialOfferFormState = (card: ICommercialOfferCard): CommercialOfferFormState => ({
    applicationID: card.applicationID || '',
    siteDbName: card.siteDbName || DEFAULT_COMMERCIAL_OFFER_FORM_STATE.siteDbName,
    commercialOffersStatusID: card.commercialOffersStatusID || '',
    commercialOfferDate: card.commercialOfferDate || '',
    commercialValidPeriodDate: card.commercialValidPeriodDate || '',
    toWhom: card.toWhom || '',
    phone: card.phone || '',
    email: card.email || '',
    comment: card.comment || '',
    dateNextCall: card.dateNextCall || '',
    timeNextCall: card.timeNextCall || '',
    pendingPrograms: [],
})

/** Анкета комерческого предложения - Контейнер */
export const CardCommercialOffersContainer = ({isOpen, cardMode, cardID, handleButtonOk, handleButtonCancel}: ICardCommercialOffersContainer) => {
    const notify = useContext(NotificationContext)

    const { 
        isLoadingCard, 
        actionGetCommercialOfferCard, actionSetCommercialOfferCard, actionGetCommercialOfferFiles,
        actionSetCommercialOfferProgramCardDelete, actionSetCommercialOfferProgramCardSave,
        actionSetCommercialOfferUploadFiles, actionSetCommercialOfferDeleteFiles,
        actionSetCommercialOfferGenerateDocument, actionSendTelegramMessageClientKP
    } = useCommercialOffers(useShallow((state) => ({
        isLoadingCard: state.isLoadingCard,
        actionGetCommercialOfferCard: state.actionGetCommercialOfferCard,
        actionSetCommercialOfferCard: state.actionSetCommercialOfferCard,
        actionGetCommercialOfferFiles: state.actionGetCommercialOfferFiles,
        actionSetCommercialOfferProgramCardDelete: state.actionSetCommercialOfferProgramCardDelete,
        actionSetCommercialOfferProgramCardSave: state.actionSetCommercialOfferProgramCardSave,
        actionSetCommercialOfferUploadFiles: state.actionSetCommercialOfferUploadFiles,
        actionSetCommercialOfferDeleteFiles: state.actionSetCommercialOfferDeleteFiles,
        actionSetCommercialOfferGenerateDocument: state.actionSetCommercialOfferGenerateDocument,
        actionSendTelegramMessageClientKP: state.actionSendTelegramMessageClientKP
    })))

    const {actionGetSitesDbName} = useDict(useShallow(state => state))

    const modalPreview = useValue<IModalPreview>({isOpen: false})
    const modalConfirm = useValue<IModalConfirm>({isOpen: false})
    const modalProgram = useValue<ICardCommercialOffersProgramContainer>({isOpen: false})
    const modalCommercialOffersGenerateDoc = useValue<ICardCommercialOffersGenerateDocContainer>({isOpen: false})

    const tabActive = useValue<string>('primary')
    const fieldsError = useValue<IFieldsError>({})
    const currentCardID = useValue<number>(0)
    const currentCardMode = useValue<CardModeType>('new')
    const isDisableBtnSave = useValue<boolean>(true)
    const savedFormSnapshotRef = useRef(createCommercialOfferFormSnapshot(DEFAULT_COMMERCIAL_OFFER_FORM_STATE))
    const hasSavedChangesRef = useRef(false)
    const saveGuard = useCardSaveGuard(isOpen, `${cardMode}:${cardID}`, () => {
        hasSavedChangesRef.current = true
        notify && notifyOpen('Результат сохранения не подтверждён. Анкета могла быть создана. Повторное создание заблокировано; закройте анкету и проверьте список.', 'warning', 10000, notify)
    })
    const isFormReadyRef = useRef(false)

    const txtApplicationID = useValue<number | ''>('')
    const dbNameList = useValue<ISitesDbNameList[]>([])
    const dbNameSelected = useValue<string>('modern_academy')
    const txtCommercialOffersStatusID = useValue<number | ''>('')
    const txtCommercialOffersStatusName = useValue<string>('')
    const txtCommercialOfferDate = useValue<string>('')
    const txtCommercialValidPeriodDate = useValue<string>('')
    const txtToWhom = useValue<string>('')
    const txtPhone = useValue<string>('')
    const txtEmail = useValue<string>('')
    const txtComment = useValue<string>('')
    const txtDateNextCall = useValue<string>('')
    const txtTimeNextCall = useValue<string>('')
    const txtDateCreate = useValue<string>('')

    const programsList = useValue<CommercialOfferCardProgramsListType[]>([])
    const filesList = useValue<CommercialOfferCardFilesListType[]>([])

    //================================================================================

    const getCurrentFormState = (): CommercialOfferFormState => ({
        applicationID: txtApplicationID.value,
        siteDbName: dbNameSelected.value,
        commercialOffersStatusID: txtCommercialOffersStatusID.value,
        commercialOfferDate: txtCommercialOfferDate.value,
        commercialValidPeriodDate: txtCommercialValidPeriodDate.value,
        toWhom: txtToWhom.value,
        phone: txtPhone.value,
        email: txtEmail.value,
        comment: txtComment.value,
        dateNextCall: txtDateNextCall.value,
        timeNextCall: txtTimeNextCall.value,
        pendingPrograms: currentCardMode.value === 'new' ? programsList.value : [],
    })

    const applyCardData = (commercialOfferID: number, card: ICommercialOfferCard) => {
        const formState = getCommercialOfferFormState(card)

        currentCardID.setValue(commercialOfferID)
        currentCardMode.setValue('edit')
        txtApplicationID.setValue(formState.applicationID)
        dbNameSelected.setValue(formState.siteDbName)
        txtCommercialOffersStatusID.setValue(formState.commercialOffersStatusID)
        txtCommercialOffersStatusName.setValue(card.commercialOffersStatusName)
        txtCommercialOfferDate.setValue(formState.commercialOfferDate)
        txtCommercialValidPeriodDate.setValue(formState.commercialValidPeriodDate)
        txtToWhom.setValue(formState.toWhom)
        txtPhone.setValue(formState.phone)
        txtEmail.setValue(formState.email)
        txtComment.setValue(formState.comment)
        txtDateNextCall.setValue(formState.dateNextCall)
        txtTimeNextCall.setValue(formState.timeNextCall)
        txtDateCreate.setValue(moment(card.dateCreate).format('DD.MM.YYYY'))
        programsList.setValue(card.programsList)
        filesList.setValue(card.filesList)

        savedFormSnapshotRef.current = createCommercialOfferFormSnapshot(formState)
        isFormReadyRef.current = true
        isDisableBtnSave.setValue(true)
    }

    useEffect(() => {
        let isCurrentRequest = true

        if (isOpen) {
            clearCard()
            const requestedCardMode = cardMode || 'new'
            const requestedCardID = Number(cardID) || 0
            currentCardID.setValue(requestedCardID)
            currentCardMode.setValue(requestedCardMode)

            const initialCommercialOfferDate = moment(new Date().toISOString()).format('DD.MM.YYYY')
            if (requestedCardMode === 'new') {
                txtCommercialOffersStatusID.setValue(1)
                txtCommercialOffersStatusName.setValue('Создан')
                txtCommercialOfferDate.setValue(initialCommercialOfferDate)
            }

            actionGetSitesDbName().then((res) => {
                if (!isCurrentRequest) return
                dbNameList.setValue(res)
                if (requestedCardMode === 'new') {
                    const selectedDbName = res[0]?.siteDbName || DEFAULT_COMMERCIAL_OFFER_FORM_STATE.siteDbName
                    dbNameSelected.setValue(selectedDbName)
                    savedFormSnapshotRef.current = createCommercialOfferFormSnapshot({
                        ...DEFAULT_COMMERCIAL_OFFER_FORM_STATE,
                        siteDbName: selectedDbName,
                        commercialOffersStatusID: 1,
                        commercialOfferDate: initialCommercialOfferDate,
                    })
                    isFormReadyRef.current = true
                    isDisableBtnSave.setValue(true)
                }
            })

            requestedCardMode === 'edit' && actionGetCommercialOfferCard(requestedCardID).then((res) => {
                if (!isCurrentRequest) return
                if (res) applyCardData(requestedCardID, res)
            })
        }

        !isOpen && clearCard() // Обнуление данных по анкете

        return () => {
            isCurrentRequest = false
        }
    }, [isOpen, cardID, cardMode]) // eslint-disable-line react-hooks/exhaustive-deps

    const currentFormSnapshot = createCommercialOfferFormSnapshot(getCurrentFormState())
    const setIsDisableBtnSave = isDisableBtnSave.setValue

    useEffect(() => {
        if (!isOpen || !isFormReadyRef.current) {
            setIsDisableBtnSave(true)
            return
        }

        setIsDisableBtnSave(currentFormSnapshot === savedFormSnapshotRef.current)
    }, [isOpen, currentFormSnapshot, setIsDisableBtnSave])

    //================================================================================

    /** Функция обнуления данных по карточке */
    const clearCard = () => {
        tabActive.setValue('primary')
        fieldsError.setValue({})
        currentCardID.setValue(0)
        currentCardMode.setValue('new')
        isDisableBtnSave.setValue(true)
        savedFormSnapshotRef.current = createCommercialOfferFormSnapshot(DEFAULT_COMMERCIAL_OFFER_FORM_STATE)
        hasSavedChangesRef.current = false
        isFormReadyRef.current = false

        txtApplicationID.setValue('')
        dbNameList.setValue([])
        dbNameSelected.setValue('modern_academy')
        txtCommercialOffersStatusID.setValue('')
        txtCommercialOffersStatusName.setValue('')
        txtCommercialOfferDate.setValue('')
        txtCommercialValidPeriodDate.setValue('')
        txtToWhom.setValue('')
        txtPhone.setValue('')
        txtEmail.setValue('')
        txtComment.setValue('')
        txtDateNextCall.setValue('')
        txtTimeNextCall.setValue('')
        txtDateCreate.setValue('')
        
        programsList.setValue([])
        filesList.setValue([])
    }

    /** Получение выбранной вкладки по имени */
    const getCardPage = (tabActive: string) => {
        switch (tabActive) {
            case 'primary': return <CardCommercialOfferTabPrimary {...propsToTabPrimary} />
            // case 'help': return <CardApplicationTabHelp {...propsToTabHelp} />
            // case 'logs': return <CardApplicationTabLogs {...propsToLogs} />
        }
    }

    /** Добавление или обновление записи в массиве программы */
    const addOrUpdateProgram = (newProgram: CommercialOfferCardProgramsListType) => {
        const { value: programs } = programsList
    
        // Проверяем, существует ли запись с таким же commercialOfferProgramID
        const existingIndex = programs.findIndex(
            (program) => program.commercialOfferProgramID === newProgram.commercialOfferProgramID
        )
    
        // Проверяем на дубликаты, игнорируя записи с совпадающим commercialOfferProgramID
        const isDuplicate = programs.some(
            (program) =>
                program.commercialOfferProgramID !== newProgram.commercialOfferProgramID && // Игнорируем текущую запись
                program.programName === newProgram.programName &&
                program.attestationTypeID === newProgram.attestationTypeID &&
                program.documentTypeID === newProgram.documentTypeID &&
                program.durationEducation === newProgram.durationEducation &&
                program.volumeHours === newProgram.volumeHours
        )
    
        if (isDuplicate) {
            return 'Дубликат записи. Запись не была добавлена.'
        }
    
        if (existingIndex !== -1) {
            // Если запись существует, обновляем её
            programs[existingIndex] = { ...programs[existingIndex], ...newProgram }
        } else {
            // Если записи нет, добавляем новую
            programs.push(newProgram)
        }
    
        // Обновляем массив в programsList
        programsList.value = [...programs]
    }

    /** При выборе данных из справочника */
    const dictSelectorData = (dictName: DictNameType, data: {id: number, name: string, param1?: string, param2?: string, param3?: string, param4?: string, param5?: string, param6?: string}) => {
        switch (dictName) {
            case 'applicationsNumber': {
                txtApplicationID.setValue(Number(data.id))
                break
            }

            case 'commercialOffersStatus': {
                txtCommercialOffersStatusID.setValue(Number(data.id))
                txtCommercialOffersStatusName.setValue(data.name)
                break
            }
        }
    }

    /** При выборе даты из календаря */
    const selectingCalendarDate = (fieldName: 'commercialOfferDate' | 'commercialValidPeriodDate' | 'dateNextCall', stringDate: string) => {
        switch (fieldName) {
            case 'commercialOfferDate': {
                txtCommercialOfferDate.setValue(stringDate)
                txtCommercialValidPeriodDate.setValue(CommonService.getEndOfMonthAfterAddingMonths(stringDate))
                break
            }

            case 'commercialValidPeriodDate': txtCommercialValidPeriodDate.setValue(stringDate); break
            case 'dateNextCall': txtDateNextCall.setValue(stringDate); break
        }
    }

    /** Активация анимации ошибки в указанных полях */
    const fieldsErrorAnumation = useFieldsErrorAnimation<IFieldsError>(fieldsError.setValue)

    //================================================================================

    /** Открытие/закрытие окна подтверждения */
    const closeCard = () => {
        modalConfirm.setValue({isOpen: false})

        if (hasSavedChangesRef.current) {
            handleButtonOk && handleButtonOk()
        } else {
            handleButtonCancel && handleButtonCancel()
        }
    }

    const handlerOpenModalConfirm = (isOpen: boolean, headerText: string, contentText: string, actionName?: string, recordID?: number) => {
        modalConfirm.setValue({
            isOpen, headerText, contentText,
            cancelButtonText: actionName === 'closeForm' ? 'Продолжить редактирование' : undefined,
            okButtonText: actionName === 'closeForm' ? 'Закрыть без сохранения' : undefined,
            handleButtonCancel() { modalConfirm.setValue({isOpen: false}) }, 
            handleButtonOk() {
                switch (actionName) {
                    case 'deleteProgram': {
                        handleDeleteProgram(recordID || 0)
                        break
                    } // Удаление программы из коммерческого предложения

                    case 'sendedTelegram': {
                        handleSendedTelegram()
                        break
                    } // Отправка информации в telegram

                    case 'closeForm': {
                        closeCard()
                        break
                    }
                }

                modalConfirm.setValue({isOpen: false})
            }
        })
    }

    /** Удаление программы */
    const handleDeleteProgram = async (commercialOfferProgramID: number) => {
        const { value: programs } = programsList

        if (currentCardMode.value === 'edit') {
            const status = await actionSetCommercialOfferProgramCardDelete(commercialOfferProgramID)
            if (status === 200) hasSavedChangesRef.current = true
        }

        // Фильтруем массив, исключая запись с указанным commercialOfferProgramID
        const updatedPrograms = programs.filter(
            (program) => program.commercialOfferProgramID !== commercialOfferProgramID
        )
    
        // Обновляем массив в programsList
        programsList.setValue(updatedPrograms)
    }

    /** Открытие/закрытие окна создания/редактирования прогграммы */
    const handleOpenModalProgram = (isOpen: boolean, cardModeProgram: CardModeType, cardData?: CommercialOfferCardProgramsListType) => {
        modalProgram.setValue({
            isOpen, cardMode: cardModeProgram, dbName: dbNameSelected.value, cardData,
            handleButtonCancel() { modalProgram.setValue({ isOpen: false }) }, 
            
            async handleButtonOk(cardData) {
                let insertedCommercialOfferProgramID = cardModeProgram === 'new' ? programsList.value.length * 10 + 1 : cardData.commercialOfferProgramID

                if (currentCardMode.value === 'edit') {
                    await actionSetCommercialOfferProgramCardSave(currentCardID.value, {
                        actionForm: cardModeProgram || 'new',
        
                        commercialOfferProgramID: cardData.commercialOfferProgramID,
    
                        programName: cardData.programName,
                        programNameActual: cardData.programNameActual,
                        
                        attestationTypeID: cardData.attestationTypeID,
                        documentTypeID: cardData.documentTypeID,
                    
                        volumeHours: cardData.volumeHours,
                        durationEducation: cardData.durationEducation,
                        price: cardData.price,
                        listenersCount: cardData.listenersCount,
                    }).then((res) => {
                        if (res.status === 200) {
                            insertedCommercialOfferProgramID = Number(res.data?.insertedID)
                            hasSavedChangesRef.current = true
                            notify && notifyOpen('Данные успешно сохранены.', 'success', 1500, notify)
                        }
                    })
                }

                const result = addOrUpdateProgram({
                    ...cardData, 
                    commercialOfferProgramID: insertedCommercialOfferProgramID
                })

                if (result) {
                    notify && notifyOpen(result, 'warning', 3000, notify)
                } else {
                    modalProgram.setValue({isOpen: false})
                }
                
            }
        })
    }

    // Загрузка файлов на сервер
    const handleFileUploader = async (files: File[]) => {
        // const maxSize = 10485760 // Максимально разрешенный размер файла для загрузки (10 MB)

        await actionSetCommercialOfferUploadFiles(currentCardID.value, files).finally(() => {
            actionGetCommercialOfferFiles(currentCardID.value).then((files) => {
                filesList.setValue(files.map((item) => ({
                    commercialOfferFileID: item.commercialOfferFileID,
                    fileName: item.fileName,
                    filePath: item.filePath,
                    fileSize: item.fileSize,
                    dateCreate: item.dateCreate
                })))
            })
        })
    }    

    /** Открытие/закрытие окна предпросмотра документа */
    const handleOpenModalPreview = (isOpen: boolean, fileUrl: string, fileName: string) => {
        modalPreview.setValue({
            isOpen, fileUrl, fileName, 
            handleButtonCancel() {modalPreview.setValue({isOpen: false})}, 
        })
    }

    /** Функция по скачиванию файлов слушателя */
    const handleDownloadFiles = (fileID?: number) => {
        // let selectedFiles: IFilesList[] = []

        // if (cardMode === 'new') return notify && notifyOpen(`Не возможно скачать файл или файлы, т.к. они еще не загружены на сервер.`, 'warning', 1500, notify)

        // if (fileID) { selectedFiles = filesList.value.filter(file => file.commercialOfferFileID === fileID) } 
        // else { selectedFiles = filesList.value.filter(file => file.isSelected) }

        for (const file of filesList.value) {
            if (file.commercialOfferFileID === fileID) {
                const link = document.createElement('a') // Создание ссылки и программное скачивание файла

                link.href = file.filePath
                link.download = file.fileName
                document.body.appendChild(link)

                link.click()
                document.body.removeChild(link)
            }
        }
    }

    /** Функция по удалению файлов слушателя */
    const handleDeleteFiles = async (fileID: number) => {
        await actionSetCommercialOfferDeleteFiles(currentCardID.value, fileID).finally(() => {
            actionGetCommercialOfferFiles(currentCardID.value).then((files) => {
                filesList.setValue(files.map((item) => ({
                    commercialOfferFileID: item.commercialOfferFileID,
                    fileName: item.fileName,
                    filePath: item.filePath,
                    fileSize: item.fileSize,
                    dateCreate: item.dateCreate,
                    isSelected: false
                })))
            })
        })
    }

    /** Открытие/закрытие окна по выбору документов для формирования */
    const handleOpenModalCommercialOffersGenerateDoc = (isOpen: boolean) => {
        modalCommercialOffersGenerateDoc.setValue({
            isOpen, 
            handleButtonCancel() {modalCommercialOffersGenerateDoc.setValue({isOpen: false})}, 
            handleButtonOk(documents) {
                // loadingTitle.setValue(`Создаем документы...`)

                actionSetCommercialOfferGenerateDocument(currentCardID.value, documents).then((res) => {
                    if (res === 200) {
                        notify && notifyOpen(`Документы успешно созданы`, 'success', 1500, notify)
                        
                        actionGetCommercialOfferFiles(currentCardID.value).then((files) => {
                            filesList.setValue(files.map((item) => ({
                                commercialOfferFileID: item.commercialOfferFileID,
                                fileName: item.fileName,
                                filePath: item.filePath,
                                fileSize: item.fileSize,
                                dateCreate: item.dateCreate,
                                isSelected: false
                            })))
                        })
                    }
                })
                // .finally(() => { loadingTitle.setValue('Загрузка анкеты...') })

                modalCommercialOffersGenerateDoc.setValue({isOpen: false})
            }
        })
    }

    /** Отправка в telegram */
    const handleSendedTelegram = async () => {
        currentCardMode.value === 'edit' && await actionSendTelegramMessageClientKP(currentCardID.value)
    }

    const handleBtnClose = () => {
        if (isLoadingCard || modalConfirm.value.isOpen) return

        if (isDisableBtnSave.value) {
            closeCard()
            return
        }

        handlerOpenModalConfirm(
            true,
            'Сохранение формы',
            '<b>Внимание!</b> Имеются не сохранённые данные!<br/><br/>Вы действительно хотите закрыть текущее окно, без сохранения?',
            'closeForm'
        )
    }

    /** Кнопка сохранить все данные во вкладке primary */
    const handleBtnSave = (isClosedModal: boolean) => {
        const validateData = validFormData([ // Валидируем данные 
            {fieldName: 'commercialOfferDate', value: txtCommercialOfferDate.value, validType: 'date', isRequired: true, errorMessage: 'Дата КП не указана или не корректна.'},
            {fieldName: 'commercialValidPeriodDate', value: txtCommercialValidPeriodDate.value, validType: 'date', isRequired: true, errorMessage: 'Срок действия КП не указан или не корректное значение.'},

            {fieldName: 'commercialOffersStatusName', value: txtCommercialOffersStatusName.value, validType: 'notEmpty', isRequired: true, errorMessage: 'Статус КП, обязательно должен быть указан.'},
            {fieldName: 'dateNextCall', value: txtDateNextCall.value, validType: 'date', isRequired: txtCommercialOffersStatusID.value === 2 ? true : false, errorMessage: 'Дата отложенного звонка не корректна или такой даты не существует.'},
            {fieldName: 'timeNextCall', value: txtTimeNextCall.value, validType: 'time', isRequired: false, errorMessage: 'Время отложенного звонка не корректное или такого времени не сущесвует.'},
        ])

        for (const item of validateData) { // Выводим ошибки валидации
            fieldsErrorAnumation(item.fieldName as FieldsErrorType)
            notify && notifyOpen(item.message, 'warning', 2000, notify)
        }

        if (programsList.value.length === 0) {
            notify && notifyOpen('Необходимо добавить хотя бы одну программу.', 'warning', 2000, notify)
            return
        }

        if (validateData.length === 0) { // Сохраняем данные
            const formState = getCurrentFormState()
            saveGuard.runSave(() => actionSetCommercialOfferCard(currentCardID.value, {
                actionForm: currentCardMode.value,
                siteDbName: formState.siteDbName,

                applicationID: Number(formState.applicationID),
                commercialOffersStatusID: Number(formState.commercialOffersStatusID),
                commercialOfferDate: formState.commercialOfferDate,
                commercialValidPeriodDate: formState.commercialValidPeriodDate,

                toWhom: formState.toWhom,
                phone: formState.phone,
                email: formState.email,
                comment: formState.comment,
                dateNextCall: formState.dateNextCall,
                timeNextCall: formState.timeNextCall,

                programsList: programsList.value
            }), {isNew: currentCardMode.value === 'new', getSavedID: response => response.data?.commercialOfferID}).then((res) => {
                if (res?.status === 200) {
                    const savedCommercialOfferID = Number(res.data?.commercialOfferID || currentCardID.value)
                    if (!Number.isSafeInteger(savedCommercialOfferID) || savedCommercialOfferID <= 0) {
                        notify && notifyOpen('Сервер не вернул идентификатор сохранённого коммерческого предложения.', 'error', 2000, notify)
                        return
                    }

                    notify && notifyOpen('Данные успешно сохранены.', 'success', 1500, notify)
                    hasSavedChangesRef.current = true

                    if (isClosedModal) {
                        handleButtonOk && handleButtonOk()
                        return
                    }

                    currentCardID.setValue(savedCommercialOfferID)
                    currentCardMode.setValue('edit')
                    savedFormSnapshotRef.current = createCommercialOfferFormSnapshot({...formState, pendingPrograms: []})
                    isDisableBtnSave.setValue(true)
                    actionGetCommercialOfferCard(savedCommercialOfferID).then((card) => {
                        if (card) applyCardData(savedCommercialOfferID, card)
                    })
                }
            })
        }
    }

    //================================================================================
    
    /** Свойства передаваемые в компоненту */
    const propsToComponent: ICardCommercialOffers = {
        isOpen, cardMode: currentCardMode.value, tabActive, isLoadingCard,
        isDisableBtnSave: {...isDisableBtnSave, value: isDisableBtnSave.value || saveGuard.isBlocked},
        siteDbName: dbNameSelected.value,

        cardID: currentCardID.value || '',
        txtDateCreate,
        
        getCardPage, handleBtnSave, handleBtnClose,
        handleOpenModalCommercialOffersGenerateDoc, handlerOpenModalConfirm
    }

    /** Свойства передаваемые в компоненту главной вкладки */
    const propsToTabPrimary: ICardCommercialOffersTabPrimary = {
        cardMode: currentCardMode.value, fieldsError,

        txtApplicationID,
        dbNameList: dbNameList.value, dbNameSelected,
        txtCommercialOffersStatusID,
        txtCommercialOffersStatusName,
        txtCommercialOfferDate,
        txtCommercialValidPeriodDate,
        txtToWhom,
        txtPhone,
        txtEmail,
        txtComment,
        txtDateNextCall,
        txtTimeNextCall,

        programsList,
        filesList,

        dictSelectorData, selectingCalendarDate,
        handlerOpenModalConfirm, 
        handleOpenModalProgram, handleFileUploader, handleOpenModalPreview,
        handleDownloadFiles, handleDeleteFiles
    }
    
    return <>
        <CardCommercialOffers {...propsToComponent} />        
        <CardCommercialOffersProgramContainer {...modalProgram.value} />
        <CardCommercialOffersGenerateDocContainer {...modalCommercialOffersGenerateDoc.value}/>

        <ModalPreview {...modalPreview.value} />
        <ModalConfirm {...modalConfirm.value} />
    </>
}